import { useCallback } from "react"
import { useDispatch } from "react-redux"
import { Creators as ApiWrapperCreators } from "model/apiWrapper"
import useResource from "hooks/resources"
import { useAccountAlias } from "resources/account"
import { ResourceResult } from "helpers/data-statuses"
import { Uuid } from "helpers/types/uuid"
import type { LayoutConfigV1 } from "connected-components/dashboard/contracts/layouts"

// A layout row from GET /api/:account/dashboard/layouts. `config` is server-validated
// LayoutConfigV1 (see contracts/layouts ADR 0003 — the client trusts it).
// The visibility tier the row belongs to (server enum). "op" rows are global templates, "account"
// rows are shared within the account, "user" rows are the caller's private layouts. Server-owned
// (knex-stringcase camelizes owner_type → ownerType); the client trusts it (ADR 0003).
export type LayoutOwnerType = "user" | "account" | "op"

// Save-time tiers only — "op" (template) layouts aren't user-authored. (APP-2690)
export type SaveLayoutTier = Extract<LayoutOwnerType, "user" | "account">

// Layouts list cache key. Reset after any path that mints a layout, or the new row won't appear in the picker.
export const LAYOUTS_RESOURCE = "layouts"

export interface Layout {
  id: Uuid
  name: string
  version: number
  ownerType: LayoutOwnerType
  config: LayoutConfigV1
}

// `enabled` (default true) lets a caller mounted ahead of need — e.g. a dialog that's rendered but
// not yet open — defer the fetch until it actually needs the list. [APP-3309]
export function useLayouts(enabled = true): ResourceResult<Layout[]> {
  const account = useAccountAlias()

  return useResource<Layout[]>({
    resource: LAYOUTS_RESOURCE,
    url: () => (enabled && account) ? `/api/${account}/dashboard/layouts` : null,
  }, LAYOUTS_RESOURCE)
}

export function useLayout(layoutId: string | null): ResourceResult<Layout> {
  const account = useAccountAlias()

  return useResource<Layout>({
    resource: "layout",
    url: () => account && layoutId ? `/api/${account}/dashboard/layouts/${layoutId}` : null,
  }, "layout")
}

// Imperative GET-by-id, for the CAS retry path: after a 409 the caller needs the layout's
// CURRENT version (the cached useLayout result is stale by definition).
export function useFetchLayout() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (layoutId: string): Promise<Layout> =>
      new Promise<Layout>((resolve, reject) => {
        if (!account) return reject(new Error("No account selected"))
        dispatch(ApiWrapperCreators.get(`/api/${account}/dashboard/layouts/${layoutId}`, resolve, reject))
      }),
    [account, dispatch],
  )
}

// Creates a layout (server assigns id + initial version); defaults to ownerType "user". (APP-2690)
export function useCreateLayout() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (name: string, config: LayoutConfigV1, ownerType: LayoutOwnerType = "user"): Promise<Layout> =>
      new Promise<Layout>((resolve, reject) => {
        if (!account) return reject(new Error("No account selected"))
        dispatch(ApiWrapperCreators.post(`/api/${account}/dashboard/layouts`, { ownerType, name, config }, resolve, reject))
      }),
    [account, dispatch],
  )
}

// CAS write: `expectedVersion` lets the server reject a stale overwrite (409).
export function useSaveLayoutConfig() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (layoutId: string, config: LayoutConfigV1, expectedVersion: number) =>
      new Promise((resolve, reject) => {
        if (!account) return reject(new Error("No account selected"))
        dispatch(ApiWrapperCreators.put(`/api/${account}/dashboard/layouts/${layoutId}`, { config, expectedVersion }, resolve, reject))
      }),
    [account, dispatch],
  )
}
